import { generateText as geminiGenerateText } from '@/lib/gemini';
import { generateText as anthropicGenerateText } from '@/lib/anthropic';
import { trackUsage } from '@/utils/costTracker';
import type { DeepAnalysisResult, ReadinessInput } from './types';
import { DEADWEIGHT_TERMS, TERM_SUBSTITUTIONS } from './vocabularyData';
import { openaiCompatFetch } from '../../utils/openaiProxyFetch';
import {
  getProvider, getGeminiKey, getGeminiModel,
  getClaudeKey, getClaudeProxy, getClaudeModel,
  getOpenAIKey, getOpenAIModel, getXAIKey, getXAIModel,
} from '@/services/llmShared';

const SYSTEM_PROMPT = `You are a Suno AI behavior expert. Suno is a pattern-matching music generation model, NOT an instruction-following assistant. It was trained on musical audio and responds to vocabulary it has strong training associations with.

Your job: analyze a user's style prompt and predict how Suno will actually interpret it.

Key principles:
- Genre anchor terms (e.g. "southern rock", "doom metal", "bossa nova") create the STRONGEST signal
- Behavioral/evocative language (e.g. "gritty", "soaring", "haunting") influences mood and texture
- DAW/engineering terms (e.g. "sidechain compression", "EQ curve", "LUFS") are DEAD WEIGHT — Suno has zero training association
- Conflicting genre signals cancel each other out (e.g. "death metal" + "bossa nova")
- Suno weights early tokens most heavily — lead with your strongest descriptors
- Scene/atmosphere cues (e.g. "midnight", "roadhouse", "smoky") provide useful context

Respond with ONLY valid JSON matching this exact schema:
{
  "optimizedPrompt": "rewritten prompt with dead-weight removed and strong terms front-loaded",
  "sunoWillPickUp": ["3-5 specific terms/phrases Suno will respond to strongly"],
  "sunoWillIgnore": ["3-5 specific terms/phrases Suno has no training association for"],
  "termSignals": [
    { "term": "term from prompt", "impact": "high|medium|low|none", "reason": "why Suno responds or doesn't", "suggestion": "optional replacement" }
  ],
  "explanation": "2-3 sentence plain-English summary of what Suno will actually produce from this prompt"
}`;

function buildUserPrompt(input: ReadinessInput): string {
  const deadWeightInPrompt = DEADWEIGHT_TERMS.filter(d => input.styleText.toLowerCase().includes(d.toLowerCase()));
  const subsAvailable = deadWeightInPrompt
    .filter(d => TERM_SUBSTITUTIONS[d])
    .map(d => `"${d}" → "${TERM_SUBSTITUTIONS[d]}"`)
    .join(', ');

  return `Analyze this Suno style prompt for readiness:

STYLE TAGS: ${input.styleText}
GENRE BLENDS: ${input.blends.join(', ') || 'none'}
${input.lyricsText ? `LYRICS EXCERPT (first 500 chars): ${input.lyricsText.substring(0, 500)}` : ''}
${subsAvailable ? `\nKNOWN SUBSTITUTIONS AVAILABLE: ${subsAvailable}` : ''}

Analyze term-by-term. For each meaningful term, classify its impact on Suno's output.`;
}

function parseResponse(text: string): DeepAnalysisResult {
  const cleaned = text.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');
  const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    // No closing brace found at all → the model almost certainly hit its token cap
    throw new Error('Response was cut off by the model (no closing brace). Try shortening your style prompt or switching providers.');
  }
  try {
    return JSON.parse(jsonMatch[0]) as DeepAnalysisResult;
  } catch (err) {
    // Truncation mid-array element produces "Expected ',' or ']' after array element"
    // and similar. Surface a useful hint instead of the raw parser message.
    const detail = err instanceof Error ? err.message : 'unknown parse error';
    throw new Error(`Deep analysis response could not be parsed — likely truncated by the model. Try shortening your style prompt or switching providers. (${detail})`);
  }
}

async function analyzeWithGemini(input: ReadinessInput): Promise<DeepAnalysisResult> {
  const apiKey = getGeminiKey();
  if (!apiKey) throw new Error('Gemini API key not configured.');
  const model = getGeminiModel();

  const { text, inputTokens, outputTokens } = await geminiGenerateText({
    apiKey,
    model,
    prompt: buildUserPrompt(input),
    systemInstruction: SYSTEM_PROMPT,
    temperature: 0.4,
    maxOutputTokens: 4000,
  });
  trackUsage({ provider: 'gemini', model, feature: 'suno-readiness', inputTokens, outputTokens });

  if (!text) throw new Error('Empty response from Gemini.');
  return parseResponse(text);
}

async function analyzeWithClaude(input: ReadinessInput): Promise<DeepAnalysisResult> {
  const apiKey = getClaudeKey();
  if (!apiKey) throw new Error('Claude API key not configured.');
  const model = getClaudeModel();

  const { text, inputTokens, outputTokens } = await anthropicGenerateText({
    apiKey,
    baseURL: getClaudeProxy(),
    model,
    systemPrompt: SYSTEM_PROMPT,
    userPrompt: buildUserPrompt(input),
    temperature: 0.4,
    maxTokens: 4000,
  });
  trackUsage({ provider: 'claude', model, feature: 'suno-readiness', inputTokens, outputTokens });
  return parseResponse(text);
}

async function analyzeWithOpenAI(input: ReadinessInput): Promise<DeepAnalysisResult> {
  const apiKey = getOpenAIKey();
  if (!apiKey) throw new Error('OpenAI API key not configured.');

  const response = await openaiCompatFetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
    body: JSON.stringify({
      model: getOpenAIModel(),
      temperature: 0.4,
      max_tokens: 4000,
      response_format: { type: 'json_object' },
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: buildUserPrompt(input) }
      ]
    })
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error?.message || 'OpenAI request failed.');

  if (data.usage) {
    trackUsage({ provider: 'openai', model: getOpenAIModel(), feature: 'suno-readiness', inputTokens: data.usage.prompt_tokens || 0, outputTokens: data.usage.completion_tokens || 0 });
  }

  return parseResponse(data.choices[0].message.content);
}

async function analyzeWithXAI(input: ReadinessInput): Promise<DeepAnalysisResult> {
  const apiKey = getXAIKey();
  if (!apiKey) throw new Error('xAI API key not configured.');

  const response = await fetch('https://api.x.ai/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
    body: JSON.stringify({
      model: getXAIModel(),
      temperature: 0.4,
      max_tokens: 4000,
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: buildUserPrompt(input) }
      ]
    })
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error?.message || 'xAI request failed.');

  if (data.usage) {
    trackUsage({ provider: 'xai', model: getXAIModel(), feature: 'suno-readiness', inputTokens: data.usage.prompt_tokens || 0, outputTokens: data.usage.completion_tokens || 0 });
  }

  return parseResponse(data.choices[0].message.content);
}

export async function runDeepAnalysis(input: ReadinessInput): Promise<DeepAnalysisResult> {
  const provider = getProvider();
  console.log(`🔬 [SunoReadiness] Deep analysis via ${provider}`);

  switch (provider) {
    case 'claude': return analyzeWithClaude(input);
    case 'openai': return analyzeWithOpenAI(input);
    case 'xai': return analyzeWithXAI(input);
    case 'gemini':
    default: return analyzeWithGemini(input);
  }
}
